import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Badge,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  TablePagination,
  Tooltip,
  alpha,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  X,
  Filter,
  Calendar,
  User,
  FileText,
  DollarSign,
  TrendingUp,
  Search,
  Trash2,
} from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { insurancetypes, claimsdetails, specialtycodes ,deleteclaim } from '../BaseURL';

const ClaimsDetails = ({ open, onClose, monthData, selectedYear }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState('all');
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [specialtyTabs, setSpecialtyTabs] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    all: 0,
    ipd: 0,
    opd: 0,
    specialties: {}
  });

  // Color palette for specialty tabs
  const colorPalette = [
    '#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a',
    '#30cfd0', '#a8edea', '#fed6e3', '#c471f5', '#11998e', '#38ef7d',
    '#2c3e50', '#3498db', '#9b59b6', '#e74c3c', '#f39c12', '#27ae60',
    '#16a085', '#8e44ad', '#c0392b', '#d35400'
  ];

  // Load insurance types and specialty codes on mount
  useEffect(() => {
    if (open) {
      console.log('Dialog opened, loading data...', { monthData, selectedYear });
      loadInsuranceTypes();
      loadSpecialtyCodes();
    }
  }, [open, monthData, selectedYear]);

  // Reload claims when filters change
  useEffect(() => {
    if (open && monthData && selectedYear) {
      console.log('Filter changed, reloading claims...', { activeTab, selectedInsurance });
      loadClaims();
    }
  }, [activeTab, selectedInsurance, open, monthData, selectedYear]);

  const loadInsuranceTypes = async () => {
    try {
      console.log('Loading insurance types from:', insurancetypes);
      const res = await fetch(insurancetypes);
      console.log('Insurance types response status:', res.status);
      
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      
      const data = await res.json();
      console.log('Insurance types data:', data);
      
      setInsuranceTypes(data);
    } catch (err) {
      console.error('Failed to load insurance types:', err);
      setError('Failed to load insurance types');
    }
  };

  const loadSpecialtyCodes = async () => {
    try {
      setLoadingSpecialties(true);
      console.log('Loading specialty codes from:', specialtycodes);
      
      const res = await fetch(specialtycodes);
      console.log('Specialty codes response status:', res.status);
      
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      
      const data = await res.json();
      console.log('Specialty codes data:', data);
      
      if (data.success && data.specialties) {
        const tabs = data.specialties.map((code, index) => ({
          code: code,
          label: formatSpecialtyLabel(code),
          color: colorPalette[index % colorPalette.length]
        }));
        console.log('Formatted specialty tabs:', tabs);
        setSpecialtyTabs(tabs);
      }
    } catch (err) {
      console.error('Failed to load specialty codes:', err);
      setSpecialtyTabs([]);
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const formatSpecialtyLabel = (code) => {
    const labelMap = {
      'MEDI': 'Medicine',
      'OBGY': 'Obstetrics & Gynecology',
      'PAED': 'Pediatrics',
      'ANC': 'Antenatal Care',
      'DENT': 'Dental',
      'ENTH': 'ENT',
      'OPDC': 'OPD Consultation',
      'SURG': 'Surgery',
      'ORTH': 'Orthopedics',
      'OPHT': 'Ophthalmology',
    };

    if (labelMap[code]) return labelMap[code];
    if (code.startsWith('ZOOM')) return code;
    return code.charAt(0) + code.slice(1).toLowerCase();
  };

  const loadClaims = async () => {
    if (!monthData || !selectedYear) {
     // console.log('Missing monthData or selectedYear, skipping load');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters
     const params = {
        year: selectedYear,              // ← ADD THIS
        monthYear: monthData,         // ← ADD THIS
        insuranceType: selectedInsurance !== 'all' ? selectedInsurance : 'all',
        };

        console.log('Params' , params)
      // Add specialty filter
      if (activeTab !== 'all' && activeTab !== 'ipd' && activeTab !== 'opd') {
        params.which_code = activeTab;
      }

      // Add IPD/OPD filter
      if (activeTab === 'ipd') {
        params.type = 'inpatient';
      } else if (activeTab === 'opd') {
        params.type = 'outpatient';
      }

      const url = `${claimsdetails}`;
      //console.log('Fetching claims from:', url, 'with params:', params);

      const res = await axios.get(url, { params });
      //console.log('Claims response:', res.data);
      
      if (res.data.success) {
       

        setClaims(res.data.claims || []);
        setStats(res.data.stats || { all: 0, ipd: 0, opd: 0, specialties: {} });
      } else {
        console.error('Backend returned success: false');
        setError('Failed to load claims data');
        setClaims([]);
      }
    } catch (err) {
      //console.error('Error loading claims:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(`Failed to load claims: ${err.message}`);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    console.log('Tab changed to:', newValue);
    setActiveTab(newValue);
    setPage(0);
  };

  const handleInsuranceChange = (event) => {
    console.log('Insurance changed to:', event.target.value);
    setSelectedInsurance(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleDeleteClaim = async (claim) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Claim?',
        html: `
          <div style="text-align: left;">
            <p><strong>Member No:</strong> ${claim.member_no}</p>
            <p><strong>Patient:</strong> ${claim.surname} ${claim.othername}</p>
            <p><strong>Date:</strong> ${formatDate(claim.d_dat)}</p>
            <p><strong>Type:</strong> ${claim.which_code}</p>
            <p><strong>Cost:</strong> ${formatCurrency(claim.which_tariff)}</p>
          </div>
          <p style="color: #d33; margin-top: 16px; font-weight: bold;">This action cannot be undone!</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        focusCancel: true,
      });

      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Make delete request to backend
        const deleteUrl = `${deleteclaim}/${claim.id}`;
        console.log('Deleting claim:', claim.id, 'from URL:', deleteUrl);
        
        const response = await axios.delete(deleteUrl);

        if (response.data.success) {
          // Remove from local state
          setClaims(prevClaims => prevClaims.filter(c => c.id !== claim.id));
          
          // Update stats
          setStats(prevStats => ({
            ...prevStats,
            all: prevStats.all - 1,
            ipd: claim.dat !== claim.d_dat ? prevStats.ipd - 1 : prevStats.ipd,
            opd: claim.dat === claim.d_dat ? prevStats.opd - 1 : prevStats.opd,
            specialties: {
              ...prevStats.specialties,
              [claim.which_code]: (prevStats.specialties[claim.which_code] || 1) - 1
            }
          }));

          // Show success message
          await Swal.fire({
            title: 'Deleted!',
            text: 'Claim has been deleted successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          throw new Error(response.data.message || 'Failed to delete claim');
        }
      }
    } catch (err) {
      console.error('Error deleting claim:', err);
      Swal.fire({
        title: 'Error!',
        text: err.response?.data?.message || err.message || 'Failed to delete claim. Please try again.',
        icon: 'error',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  const formatCurrency = (amount) => {
    return `GH₵ ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTabCount = (tabValue) => {
    if (tabValue === 'all') return stats.all || 0;
    if (tabValue === 'ipd') return stats.ipd || 0;
    if (tabValue === 'opd') return stats.opd || 0;
    return stats.specialties?.[tabValue] || 0;
  };

  // Filter claims based on search query
  const filteredClaims = claims.filter((claim) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const memberNo = (claim.member_no || '').toString().toLowerCase();
    const patientId = (claim.patient_id || '').toString().toLowerCase();
    
    return memberNo.includes(query) || patientId.includes(query);
  });

  // Paginated claims from filtered results
  const paginatedClaims = filteredClaims.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Claims for {monthData?.month} {selectedYear}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Total: {monthData?.total?.toLocaleString()} claims
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              background: 'rgba(255,255,255,0.2)',
              '&:hover': {
                background: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            <X size={24} />
          </IconButton>
        </Box>

        {/* Insurance Type Filter */}
        <Box sx={{ mt: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel
              sx={{
                color: 'white',
                '&.Mui-focused': { color: 'white' },
              }}
            >
              Filter by Insurance Type
            </InputLabel>
            <Select
              value={selectedInsurance}
              onChange={handleInsuranceChange}
              label="Filter by Insurance Type"
              startAdornment={<Filter size={18} style={{ marginRight: 8, color: 'white' }} />}
              sx={{
                color: 'white',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            >
              <MenuItem value="all">All Insurance Types</MenuItem>
              {insuranceTypes.map((type, index) => (
                <MenuItem key={type.scheme_name || index} value={type.scheme_name}>
                  {type.scheme_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: '#f8f9fd' }}>
        {/* Error Alert */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Tabs */}
        <Box
          sx={{
            background: 'white',
            borderBottom: '2px solid #e0e0e0',
            px: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 100,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
              },
              '& .Mui-selected': {
                color: '#667eea',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              value="all"
              label={
                <Badge badgeContent={getTabCount('all')} color="primary" max={999999}>
                  <span style={{ marginRight: 12 }}>ALL</span>
                </Badge>
              }
            />
            <Tab
              value="ipd"
              label={
                <Badge badgeContent={getTabCount('ipd')} color="secondary" max={999999}>
                  <span style={{ marginRight: 12 }}>IPD</span>
                </Badge>
              }
            />
            <Tab
              value="opd"
              label={
                <Badge badgeContent={getTabCount('opd')} color="success" max={999999}>
                  <span style={{ marginRight: 12 }}>OPD</span>
                </Badge>
              }
            />
            {loadingSpecialties ? (
              <Tab
                disabled
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Loading...</span>
                  </Box>
                }
              />
            ) : (
              specialtyTabs.map((specialty) => (
                <Tab
                  key={specialty.code}
                  value={specialty.code}
                  label={
                    <Badge
                      badgeContent={getTabCount(specialty.code)}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: specialty.color,
                          color: 'white',
                        },
                      }}
                      max={999999}
                    >
                      <Tooltip title={specialty.label} arrow>
                        <span style={{ marginRight: 12 }}>{specialty.code}</span>
                      </Tooltip>
                    </Badge>
                  }
                />
              ))
            )}
          </Tabs>
        </Box>

        {/* Stats Summary Bar */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
            borderBottom: '1px solid #e0e0e0',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search by Member No or Patient ID..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              minWidth: 300,
              flexGrow: 1,
              background: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: '#667eea' }} />
                </InputAdornment>
              ),
            }}
          />

          <Chip
            icon={<FileText size={16} />}
            label={`${filteredClaims.length} Claims`}
            sx={{
              fontWeight: 600,
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          />
          <Chip
            icon={<DollarSign size={16} />}
            label={`Total: ${formatCurrency(
              filteredClaims.reduce((sum, claim) => sum + parseFloat(claim.which_tariff || 0), 0)
            )}`}
            sx={{
              fontWeight: 600,
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          />
          <Chip
            icon={<TrendingUp size={16} />}
            label={`Avg: ${formatCurrency(
              filteredClaims.length > 0
                ? filteredClaims.reduce((sum, claim) => sum + parseFloat(claim.which_tariff || 0), 0) /
                    filteredClaims.length
                : 0
            )}`}
            sx={{
              fontWeight: 600,
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          />
        </Box>

        {/* Table */}
        <Box sx={{ p: 2 }}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  }}
                >
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Calendar size={16} />
                      Claim Date
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <User size={16} />
                      Member No
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    Added
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    Type
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}
                    align="right"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                      <DollarSign size={16} />
                      Cost (GHC)
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} sx={{ color: '#667eea' }} />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading claims...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="h6" color="text.secondary">
                        No claims found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try adjusting your filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedClaims.map((claim, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:hover': {
                          background: alpha('#667eea', 0.05),
                        },
                        '&:nth-of-type(even)': {
                          background: alpha('#f5f5f5', 0.5),
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(claim.d_dat)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={claim.member_no || 'N/A'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontFamily: 'monospace',
                              mb: 0.5,
                            }}
                          />
                          {claim.patient_id && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem'
                              }}
                            >
                              ID: {claim.patient_id}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {claim.surname} {claim.othername}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {claim.documenting}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={claim.which_code || 'N/A'}
                          size="small"
                          sx={{
                            background:
                              specialtyTabs.find((s) => s.code === claim.which_code)?.color ||
                              '#667eea',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {formatCurrency(claim.which_tariff)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete Claim" arrow>
                          <IconButton
                            onClick={() => handleDeleteClaim(claim)}
                            size="small"
                            sx={{
                              color: '#d32f2f',
                              '&:hover': {
                                background: alpha('#d32f2f', 0.1),
                              },
                            }}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && filteredClaims.length > 0 && (
            <TablePagination
              component="div"
              count={filteredClaims.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                mt: 2,
                background: 'white',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimsDetails;